export const UNSPLASH_SERVICE = 'unsplash'
export const CANVA_SERVICE = 'canva'
export const UPLOADCARE_SERVICE = 'uploadcare'
export const IMAGE_EDITOR_SERVICE = 'imageEditor'

enum UploadTrigger {
  FilePicker = 'filePicker',
  DragAndDrop = 'dragAndDrop',
  Paste = 'paste',
  Integration = 'integration',
  Extension = 'extension',
}

declare const otherSourceNameBrand: unique symbol

type OtherSourceName = string & {
  readonly [otherSourceNameBrand]: true
}

type OtherSource = {
  name: OtherSourceName
  trigger: UploadTrigger
}

type CanvaSource = {
  name: typeof CANVA_SERVICE
  trigger: UploadTrigger.Integration
  id: string
}

type UnsplashSource = {
  name: typeof UNSPLASH_SERVICE
  trigger: UploadTrigger.Integration
  id: string
  author: string
}

type UploadcareSource = {
  name: typeof UPLOADCARE_SERVICE
  trigger: UploadTrigger.Integration
  id: string
}

type ImageEditorSource = {
  name: typeof IMAGE_EDITOR_SERVICE
  trigger: UploadTrigger.Integration
  id: string
}

const hasProp = <K extends string>(
  obj: unknown,
  key: K,
): obj is Record<K, string> => {
  return obj !== null && typeof obj === 'object' && key in obj
}

const hasStringProp = <K extends string>(
  obj: unknown,
  key: K,
): obj is Record<K, string> => {
  return hasProp(obj, key) && typeof obj[key] === 'string'
}

const isOtherSourceName = (name: string): name is OtherSourceName =>
  name !== CANVA_SERVICE &&
  name !== UNSPLASH_SERVICE &&
  name !== UPLOADCARE_SERVICE &&
  name !== IMAGE_EDITOR_SERVICE

const createOtherSourceName = (name: string): OtherSourceName => {
  if (!isOtherSourceName(name)) {
    throw new TypeError(
      `Upload source "${name}" requires its dedicated factory`,
    )
  }

  return name
}

export type UploadSource =
  | CanvaSource
  | UnsplashSource
  | UploadcareSource
  | ImageEditorSource
  | OtherSource

export const UploadSource = {
  new(src: unknown): UploadSource | undefined {
    if (UploadSource.isCanva(src)) {
      return {
        name: src.name,
        id: src.id,
        trigger: UploadTrigger.Integration,
      }
    }

    if (UploadSource.isUnsplash(src)) {
      return {
        name: src.name,
        id: src.id,
        author: src.author,
        trigger: UploadTrigger.Integration,
      }
    }

    if (UploadSource.isUploadcare(src)) {
      return {
        name: src.name,
        id: src.id,
        trigger: UploadTrigger.Integration,
      }
    }

    if (UploadSource.isImageEditor(src)) {
      return {
        name: src.name,
        id: src.id,
        trigger: UploadTrigger.Integration,
      }
    }

    if (UploadSource.isOtherSource(src)) {
      return {
        name: src.name,
        trigger: src.trigger,
      }
    }
  },

  dragAndDrop(): OtherSource {
    return {
      name: createOtherSourceName('localFile'),
      trigger: UploadTrigger.DragAndDrop,
    }
  },

  paste(): OtherSource {
    return {
      name: createOtherSourceName('localFile'),
      trigger: UploadTrigger.Paste,
    }
  },

  filePicker(): OtherSource {
    return {
      name: createOtherSourceName('localFile'),
      trigger: UploadTrigger.FilePicker,
    }
  },

  extension(): OtherSource {
    return {
      name: createOtherSourceName('remoteFile'),
      trigger: UploadTrigger.Extension,
    }
  },

  canva(id: string): CanvaSource {
    return {
      name: CANVA_SERVICE,
      id,
      trigger: UploadTrigger.Integration,
    }
  },

  unsplash(props: { id: string; author: string }): UnsplashSource {
    return {
      name: UNSPLASH_SERVICE,
      id: props.id,
      author: props.author,
      trigger: UploadTrigger.Integration,
    }
  },

  uploadcare(id: string): UploadcareSource {
    return {
      name: UPLOADCARE_SERVICE,
      id,
      trigger: UploadTrigger.Integration,
    }
  },

  imageEditor(id: string): ImageEditorSource {
    if (!id.trim()) throw new TypeError('Image editor source id is required')

    return {
      name: IMAGE_EDITOR_SERVICE,
      id,
      trigger: UploadTrigger.Integration,
    }
  },

  integration(name: string): OtherSource {
    return {
      name: createOtherSourceName(name),
      trigger: UploadTrigger.Integration,
    }
  },

  isOtherSource(src: unknown): src is OtherSource {
    return (
      hasStringProp(src, 'name') &&
      hasStringProp(src, 'trigger') &&
      UploadSource.isValidTrigger(src.trigger) &&
      isOtherSourceName(src.name)
    )
  },

  isCanva(src?: unknown): src is CanvaSource {
    return (
      hasStringProp(src, 'name') &&
      hasStringProp(src, 'id') &&
      src.name === CANVA_SERVICE &&
      !!src.id
    )
  },

  isUnsplash(src?: unknown): src is UnsplashSource {
    return (
      hasStringProp(src, 'name') &&
      hasStringProp(src, 'id') &&
      hasStringProp(src, 'author') &&
      src.name === UNSPLASH_SERVICE &&
      !!src.id
    )
  },

  isUploadcare(src?: unknown): src is UploadcareSource {
    return (
      hasStringProp(src, 'name') &&
      hasStringProp(src, 'id') &&
      src.name === UPLOADCARE_SERVICE &&
      !!src.id
    )
  },

  isImageEditor(src?: unknown): src is ImageEditorSource {
    return (
      hasStringProp(src, 'name') &&
      hasStringProp(src, 'id') &&
      src.name === IMAGE_EDITOR_SERVICE &&
      !!src.id.trim()
    )
  },

  isValidTrigger(trigger: string): trigger is UploadTrigger {
    return Object.values<string>(UploadTrigger).includes(trigger)
  },
}
